"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Loader, CalendarDays, Users, Clock } from "lucide-react";
export default function ReserveYourExperience() {
  return <section data-section-id="221912" id="reach-our-team" className="py-24 bg-background relative overflow-hidden">
      {}
      <div className="container mx-auto max-w-5xl px-4 relative z-10">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
            Reach Our Team
          </h2>
          <p className="text-muted-foreground text-lg font-default">
            Ready to power your next project? Provide your site details below, and our electrical infrastructure specialists will coordinate your installation.
          </p>
        </div>

        <FormProvider formKey="reach_team_form_contact" formId="reach_team_form_contact" sectionName="Reach Our Team" defaultValues={{
        full_name: "",
        phone_number: "",
        email_address: "",
        reservation_date: "",
        reservation_time: "08:00",
        guest_count: 1,
        special_requests: ""
      }} validationRules={{
        full_name: {
          required: true,
          minLength: 2
        },
        phone_number: {
          required: true,
          pattern: "phone"
        },
        email_address: {
          pattern: "email"
        },
        reservation_date: {
          required: true
        },
        reservation_time: {
          required: true
        },
        guest_count: {
          required: true,
          min: 1,
          max: 20
        }
      }} submitText="Submit Request" successMessage="Project details received. A Gridspan engineer will review your site requirements and contact you shortly." className="w-full">
          <ReservationFormContent />
        </FormProvider>
      </div>
    </section>;
}
function ReservationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const timeOptions = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];
  if (isSubmitted && !submitError) {
    return <div className="max-w-2xl mx-auto p-12 bg-card border border-primary/30 rounded-none text-center shadow-xl">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-none bg-primary/10 text-primary mb-6">
          <CalendarDays className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground mb-4 uppercase tracking-wider">Request Logged</h3>
        <p className="text-muted-foreground mb-8 text-lg font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-none font-mono">
          Log Another Project
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="bg-card border border-border rounded-none shadow-2xl overflow-hidden">
        <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
          
          {}
          <div className="p-8 md:p-10 space-y-8">
            <div className="space-y-2">
              <h3 className="text-2xl font-default font-bold text-foreground flex items-center gap-2 uppercase tracking-tight">
                <span className="flex items-center justify-center w-8 h-8 rounded-none bg-primary text-primary-foreground text-sm font-mono font-bold">01</span>
                Site Logistics
              </h3>
              <p className="text-sm text-muted-foreground font-mono">Specify the installation timeline and scale.</p>
            </div>

            <FormField control={control} name="reservation_date" render={({
            field
          }) => <FormItem className="flex flex-col items-center sm:items-start">
                  <FormLabel className="sr-only">Preferred Start Date</FormLabel>
                  <FormControl>
                    <div className="p-3 bg-background border border-input rounded-none shadow-sm w-full flex justify-center sm:justify-start">
                      <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => {
                    if (date) {
                      const offset = date.getTimezoneOffset() * 60000;
                      const localDate = new Date(date.getTime() - offset);
                      field.onChange(localDate.toISOString().split('T')[0]);
                    } else {
                      field.onChange('');
                    }
                  }} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} className="rounded-none" /></SafeHydrate>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <div className="grid grid-cols-2 gap-4">
              <FormField control={control} name="reservation_time" render={({
              field
            }) => <FormItem>
                    <FormLabel className="flex items-center gap-2 text-foreground font-mono text-xs uppercase">
                      <Clock className="w-4 h-4 text-primary" /> Site Access
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="bg-background rounded-none border-input">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-none">
                        {timeOptions.map((time, i) => <SelectItem key={time} value={time} data-index={i}>
                            {time}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="guest_count" render={({
              field
            }) => <FormItem>
                    <FormLabel className="flex items-center gap-2 text-foreground font-mono text-xs uppercase">
                      <Users className="w-4 h-4 text-primary" /> Phase Count
                    </FormLabel>
                    <Select onValueChange={val => field.onChange(Number(val))} defaultValue={String(field.value || 1)}>
                      <FormControl>
                        <SelectTrigger className="bg-background rounded-none border-input">
                          <SelectValue placeholder="Number of phases" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="rounded-none">
                        {Array.from({
                    length: 10
                  }).map((_, i) => {
                    const num = i + 1;
                    return <SelectItem key={num} value={String(num)} data-index={i}>
                              {num} {num === 1 ? 'Phase' : 'Phases'}
                            </SelectItem>;
                  })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>

          {}
          <div className="p-8 md:p-10 space-y-8 bg-muted/30">
            <div className="space-y-2">
              <h3 className="text-2xl font-default font-bold text-foreground flex items-center gap-2 uppercase tracking-tight">
                <span className="flex items-center justify-center w-8 h-8 rounded-none bg-primary text-primary-foreground text-sm font-mono font-bold">02</span>
                Project Details
              </h3>
              <p className="text-sm text-muted-foreground font-mono">Provide technical contact and site location.</p>
            </div>

            <div className="space-y-5">
              <FormField control={control} name="full_name" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase">Project Manager <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <input type="text" placeholder="Full Name" className="flex h-10 w-full rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="phone_number" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase">Site Contact Number <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <input type="tel" placeholder="+44 000 000 000" className="flex h-10 w-full rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors" {...field} />
                    </FormControl>
                    <FormDescription className="text-[10px] font-mono uppercase">Required for site access coordination.</FormDescription>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="email_address" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase">Technical Email</FormLabel>
                    <FormControl>
                      <input type="email" placeholder="engineering@gridspan.co.uk" className="flex h-10 w-full rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="special_requests" render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase">Site Requirements</FormLabel>
                    <FormControl>
                      <textarea placeholder="e.g., Temporary power requirements, distribution board specs, site hazards..." className="flex min-h-[80px] w-full rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors resize-y" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none text-xs font-mono text-destructive">
                {submitError}
              </div>}

            <div className="pt-4">
              <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 text-lg h-14 font-default font-bold rounded-none shadow-lg transition-all uppercase tracking-widest">
                {isSubmitting ? <>
                    <Loader className="animate-spin mr-2 h-5 w-5" />
                    Processing...
                  </> : 'Submit Request'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Form>;
}